"use client"
import { useTranslations } from "next-intl"

import type React from "react"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { ChevronRight, Loader2, AlertCircle, Check, ArrowRight } from "lucide-react"
import { ProgressIndicator } from "@/components/publications/creation-flow/listing-creation/progress-indicator"
import { DockSheet } from "@/components/ui/dock-sheet"
import { SHEET_Z_NESTED } from "@/lib/sheet-styles"
import { Button } from "@/components/ui/button"
import { AnimatePresence, motion } from "framer-motion"
import { cn } from "@/lib/utils"
import { createClient } from "@/utils/supabase/client"
import { getNormalizedImageFile, getSafeImageExtension } from "@/lib/image-upload"
import { storageBucket, StoragePath } from "@/lib/storage"
import { v4 as uuidv4 } from "uuid"
import type { ListingData, ShippingData } from "@/types/listing"
import { CategoryStep } from "@/components/publications/creation-flow/listing-creation/steps/category-step"
import { ConditionStep } from "@/components/publications/creation-flow/listing-creation/steps/condition-step"
import { DetailsStep } from "@/components/publications/creation-flow/listing-creation/steps/details-step"
import { ShippingStep } from "@/components/publications/creation-flow/listing-creation/steps/shipping-step"
import { DialogCard } from "@/components/ui/dialog-card"
import { useMobile } from "@/hooks/use-mobile"
import { SimplePhotoEditor, type PhotoItem, type SimplePhotoEditorRef } from "@/components/my-listings/simple-photo-editor-production"
import { DEFAULT_CURRENCY } from "@/lib/currencies"
import {
  createListing,
  CreateListingError,
  type CreateListingIntent,
} from "@/lib/listings/listing-create"
import { asRawImageRef, isPersistedImageRef, rawImageRefValue, type RawImageRef } from "@/lib/storage/url"

type Props = {
  open: boolean
  onOpenChangeAction: (open: boolean) => void
  currentUserId: string
  roomName: string
  onShareListingAction: (listing: any) => void
}

// ADR_LISTING_SHIP_FROM_ADDRESS Phase 3 — "location" step removed.
type Step = "photos" | "category" | "details" | "condition" | "shipping"

// Canonical predicate — a bucket-relative storage path counts as persisted
// too, otherwise every freshly-uploaded image is filtered out once writers
// stop calling getPublicUrl(). @see lib/storage/url.ts
const isHttpUrl = isPersistedImageRef

export function ChatSellItemSheet({ open, onOpenChangeAction, currentUserId, onShareListingAction }: Props) {
  const tChat = useTranslations("chat");
  const router = useRouter()
  const supabase = useMemo(() => createClient(), [])
  const isMobile = useMobile()
  const [step, setStep] = useState<Step>("photos")
  const [visibleStep, setVisibleStep] = useState<Step>(() => step)
  const stepTargetRef = useRef(step)
  stepTargetRef.current = step
  const [photos, setPhotos] = useState<PhotoItem[]>([])
  const isEditMode = photos.length > 0

  const [isCheckingRequirements, setIsCheckingRequirements] = useState(true)
  const [isBanned, setIsBanned] = useState(false)
  const [isPhoneVerified, setIsPhoneVerified] = useState(false)
  const [hasCurrency, setHasCurrency] = useState(false)
  const [hasPayPalConfig, setHasPayPalConfig] = useState(false)
  const [hasShippingMethods, setHasShippingMethods] = useState(false)
  const [hasAddress, setHasAddress] = useState(false)
  const initialDraft = useMemo<ListingData>(
    () => ({
      title: "",
      description: "",
      price: undefined,
      currency: undefined,
      photos: [],
      brand_id: undefined,
      theme_id: undefined,
      product_type_id: undefined,
      theme_custom_text: undefined,
      brand_custom_text: undefined,
      product_type_custom_text: undefined,
      condition: "",
      grade: "",
      shipping: { methods: [], customOptions: [] } as ShippingData,
      quantity: 1,
    }),
    [],
  )
  const draftRef = useRef<ListingData>(initialDraft)
  const [draft, setDraft] = useState<ListingData>(() => ({
    title: "",
    description: "",
    price: undefined,
    currency: undefined,
    photos: [],
    brand_id: undefined,
    theme_id: undefined,
    product_type_id: undefined,
    theme_custom_text: undefined,
    brand_custom_text: undefined,
    product_type_custom_text: undefined,
    condition: "",
    grade: "",
    location: "",
    shipping: { methods: [], customOptions: [] } as ShippingData,
    quantity: 1,
  }))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const shippingSaveRef = useRef<(() => void) | null>(null)
  const [isShippingValid, setIsShippingValid] = useState(false)
  const [isCategoryValid, setIsCategoryValid] = useState(false)
  const [isDetailsValid, setIsDetailsValid] = useState(false)

  const photoEditorRef = useRef<SimplePhotoEditorRef>(null)

  useEffect(() => {
    if (open) return
    setStep("photos")
    setVisibleStep("photos")
    setPhotos((prev) => {
      prev.forEach((p) => {
        const preview = rawImageRefValue(p.preview)
        if (preview && preview.startsWith("blob:")) {
          try {
            URL.revokeObjectURL(preview)
          } catch {}
        }
      })
      return []
    })
    setDraft(initialDraft)
    draftRef.current = initialDraft
    setIsSubmitting(false)
    shippingSaveRef.current = null
    setIsShippingValid(false)
    setIsCheckingRequirements(true)
  }, [open])

  useEffect(() => {
    if (!open || !currentUserId) {
      if (!open) setIsCheckingRequirements(false)
      return
    }
    let isMounted = true
    setIsCheckingRequirements(true)
    ;(async () => {
      try {
        const [profileRes, paypalRes, shippingRes, addressRes] = await Promise.all([
          fetch("/api/user/profile").then((r) => r.ok ? r.json().catch(() => null) : null),
          supabase.from("profiles").select("paypal_merchant_id, currency, banned_at").eq("id", currentUserId).single(),
          supabase.from("shipping_methods").select("id").eq("user_id", currentUserId),
          supabase.from("address_book").select("id").eq("user_id", currentUserId).eq("is_main", true).is("deleted_at", null).single(),
        ])
        if (!isMounted) return
        const phone = typeof profileRes?.phone === "string" ? profileRes.phone.trim() : ""
        setIsPhoneVerified(phone.length > 0)
        const banned = Boolean((paypalRes.data as { banned_at?: string | null } | null)?.banned_at)
        setIsBanned(banned)
        setHasPayPalConfig(!paypalRes.error && !!paypalRes.data?.paypal_merchant_id)
        setHasCurrency(!paypalRes.error && !!paypalRes.data?.currency)
        setHasShippingMethods(!shippingRes.error && Array.isArray(shippingRes.data) && shippingRes.data.length > 0)
        setHasAddress(!addressRes.error && !!addressRes.data)
      } catch {
        if (isMounted) {
          setHasPayPalConfig(false)
          setHasCurrency(false)
          setHasShippingMethods(false)
          setHasAddress(false)
        }
      } finally {
        if (isMounted) setIsCheckingRequirements(false)
      }
    })()
    return () => { isMounted = false }
  }, [open, currentUserId, supabase])

  const canCreateListing = !isCheckingRequirements && !isBanned && isPhoneVerified && hasCurrency && hasPayPalConfig && hasShippingMethods && hasAddress

  const canContinue = () => {
    if (step === "photos") return photos.length > 0
    if (step === "details") return isDetailsValid
    if (step === "category") return isCategoryValid
    if (step === "condition") {
      if (draft.condition === "new") return true
      if (draft.condition === "pre-owned" || draft.condition === "custom") return Boolean(draft.grade && draft.grade.trim().length > 0)
      return false
    }
    return isShippingValid
  }

  const handleClose = () => {
    onOpenChangeAction(false)
  }

  const uploadImage = async (file: File, index: number, title: string): Promise<RawImageRef | null> => {
    let fileToUpload = file
    try {
      const normalized = await getNormalizedImageFile(file)
      fileToUpload = normalized.file
    } catch {}
    const extension = getSafeImageExtension(fileToUpload)
    const filePath = StoragePath.listing(title, index, extension)
    const { error: uploadError } = await storageBucket(supabase).upload(filePath, fileToUpload)
    if (uploadError) return null
    // Bucket-relative path -> listings.image_urls.
    return asRawImageRef(filePath)
  }

  const updateDraft = useCallback((updates: Partial<ListingData>) => {
    setDraft((prev) => {
      const next = { ...prev, ...updates }
      draftRef.current = next
      return next
    })
  }, [])

  // Ensure currency is set for the Details step (otherwise it falls back to USD -> "$")
  useEffect(() => {
    if (!open) return
    if ((draftRef.current as any)?.currency) return
    ;(async () => {
      try {
        const res = await fetch("/api/user/profile")
        if (!res.ok) return
        const profile = await res.json().catch(() => null)
        const currency = typeof profile?.currency === "string" && profile.currency ? profile.currency : DEFAULT_CURRENCY
        updateDraft({ currency })
      } catch {
        updateDraft({ currency: DEFAULT_CURRENCY })
      }
    })()
  }, [open, updateDraft])

  const buildShippingConfig = (shipping: ShippingData | undefined) => {
    const selectedMethodIds = shipping?.methods || []
    const customShippingOptions = shipping?.customOptions || []
    const configArray: any[] = []

    customShippingOptions.forEach((option: any) => {
      if (selectedMethodIds.includes(option.id)) {
        configArray.push({
          shipping_method_id: option.id,
          label: option.label || option.name,
          price: option.price,
          allow_combined: option.allowCombined || false,
          combined_fee: option.combinedFee || "0",
        })
      }
    })

    return configArray
  }

  const submitListing = async () => {
    const currentDraft = draftRef.current
    const title = currentDraft.title.trim()
    const listingId = uuidv4()
    // Persistence array — raw all the way to `createListing` (handoff §9).
    const uploadedUrls: RawImageRef[] = []
    const currentPhotos = photoEditorRef.current?.getCurrentPhotosInOrder?.() ?? photos
    for (let i = 0; i < currentPhotos.length; i++) {
      const p = currentPhotos[i]
      if (p.file instanceof File) {
        const url = await uploadImage(p.file, i, title)
        if (url) uploadedUrls.push(url)
      } else if (p.preview && isHttpUrl(p.preview)) {
        uploadedUrls.push(p.preview)
      }
    }

    const shipping_config = buildShippingConfig(currentDraft.shipping)

    const shippingMode = currentDraft.shippingMode ?? "legacy"
    const isLiveRate = shippingMode === "live_rate"
    const parcel = currentDraft.parcel

    const payload = {
      id: listingId,
      title,
      description: currentDraft.description,
      price: Number(currentDraft.price ?? 0),
      quantity: 0,
      image_urls: uploadedUrls,
      condition: currentDraft.condition || "",
      grade: currentDraft.grade || "",
      shipping_config: Array.isArray(shipping_config) ? shipping_config : [],
      brand_id: currentDraft.brand_id ?? null,
      theme_id: currentDraft.theme_id ?? null,
      product_type_id: currentDraft.product_type_id ?? null,
      theme_custom_text: currentDraft.theme_custom_text ?? null,
      brand_custom_text: currentDraft.brand_custom_text ?? null,
      product_type_custom_text: currentDraft.product_type_custom_text ?? null,
      scheduled_publish_at: null,
      is_dev_listing: false,
      visibility: "chat_private",
      chat_private_saved: false,
      shipping_mode: shippingMode,
      carrier_provider: isLiveRate ? "shippo" : null,
      parcel_weight_grams: isLiveRate ? parcel?.weight ?? null : null,
      parcel_length_mm: isLiveRate ? parcel?.length ?? null : null,
      parcel_width_mm: isLiveRate ? parcel?.width ?? null : null,
      parcel_height_mm: isLiveRate ? parcel?.height ?? null : null,
    }

    let result: { listingId: string }
    try {
      result = await createListing(payload as CreateListingIntent)
    } catch (apiErr) {
      if (apiErr instanceof CreateListingError) {
        const message = apiErr.serverError ?? "Could not create listing"
        throw new Error(message)
      }
      throw apiErr
    }
    const resolvedListingId = String(result.listingId || listingId)
    return {
      ...payload,
      id: resolvedListingId,
      user_id: currentUserId,
      shipping_config: Array.isArray(shipping_config) ? shipping_config : [],
    }
  }

  const handleSubmit = async () => {
    if (!canContinue() || isSubmitting) return
    setIsSubmitting(true)
    try {
      shippingSaveRef.current?.()
      updateDraft({ photos: photoEditorRef.current?.getCurrentPhotosInOrder?.() ?? photos })
      await new Promise(resolve => setTimeout(resolve, 0))
      const created = await submitListing()
      onShareListingAction(created)
      handleClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  const stepOrder: Step[] = ["photos", "category", "details", "condition", "shipping"]
  const stepLabels = [tChat("photos"), tChat("category"), tChat("details"), tChat("condition"), tChat("shipping")]
  const stepTitles: Record<Step, string> = {
    photos: tChat("addPhotos"),
    category: tChat("category"),
    details: tChat("itemDetails"),
    condition: tChat("itemCondition"),
    shipping: tChat("shippingOptions"),
  }
  const stepDescriptions: Record<Step, string> = {
    photos: tChat("addPhotosDesc"),
    category: tChat("selectBrandTheme"),
    details: tChat("itemDetailsDesc"),
    condition: tChat("conditionDesc"),
    shipping: tChat("shippingDesc"),
  }
  const currentStepIndex = stepOrder.indexOf(visibleStep)

  const goBack = () => {
    if (step === "category") setStep("photos")
    else if (step === "details") setStep("category")
    else if (step === "condition") setStep("details")
    else if (step === "shipping") setStep("condition")
  }

  /* Chrome is described ONCE and consumed by both primitives: `DockSheet` on
     mobile, `DialogCard` on desktop, same `title` / `onBack` / `belowTitle`
     props. Before 2026-08-01 both branches shared one hand-rolled `sheetHeader`
     (a mobile grabber leaked onto desktop); the fix at the time was a second,
     hand-rolled desktop header — which is what Phase 1.5 deleted. */
  const sheetTitle = isCheckingRequirements
    ? tChat("sellItem")
    : !canCreateListing
      ? tChat("setupRequired")
      : stepTitles[visibleStep]

  const canGoBack = !isCheckingRequirements && canCreateListing && visibleStep !== "photos"

  const sheetBelowTitle =
    !isCheckingRequirements && canCreateListing ? (
      <>
        <p className="text-foreground/70 text-sm">{stepDescriptions[visibleStep]}</p>
        <div className="flex justify-center pt-2">
          <div className="w-full max-w-sm">
            <ProgressIndicator
              steps={stepLabels}
              currentStep={currentStepIndex}
              onStepClickAction={(idx) => setStep(stepOrder[idx])}
            />
          </div>
        </div>
      </>
    ) : undefined

  const goNext = () => {
    if (step === "photos") setStep("category")
    else if (step === "category") setStep("details")
    else if (step === "details") setStep("condition")
    else if (step === "condition") setStep("shipping")
  }

  /* The CTA alone — both primitives supply the fade + padding + safe-area
     around it via their `footer` slot (canon §3.1 / OVERLAY_FOOTER.md). */
  const primaryCta = canCreateListing ? (
    <Button
      type="button"
      onClick={step === "shipping" ? handleSubmit : goNext}
      disabled={!canContinue() || (step === "shipping" && isSubmitting)}
      className="w-full py-4 text-base shadow-md rounded-full group"
      size="default"
    >
      {step === "shipping" && isSubmitting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {tChat("selling")}
        </>
      ) : step === "shipping" ? (
        tChat("sell")
      ) : (
        <>
          {tChat("continue")}
          <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </>
      )}
    </Button>
  ) : null

  const content = (
      <div className="rounded-t-2xl flex flex-col bg-sheet text-foreground min-h-0 flex-1">
        {isCheckingRequirements ? (
          <div className="flex-1 flex items-center justify-center gap-2 text-muted-foreground p-4">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>{tChat("checkingSellerSetup")}</span>
          </div>
        ) : !canCreateListing ? (
          <div className="flex-1 overflow-y-auto p-4">
            {isBanned ? (
              <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
                <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                <span className="text-sm font-medium text-red-300">
                  {tChat("accountSuspended")}{" "}
                  <Link href="/contact" className="underline hover:opacity-90">{tChat("contactSupport")}</Link>.
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-amber-400" />
                  <span className="text-sm font-medium text-amber-300">
                    {tChat("toSellNeedTo")}
                  </span>
                </div>
                <div className="space-y-2 ml-6">
                  {!isPhoneVerified && (
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <Check className="h-3 w-3 text-amber-400" />
                        <span className="text-sm text-amber-300">{tChat("verifyPhone")}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => { router.push("/verify-phone"); handleClose(); }}
                        className="group px-3 py-1.5 bg-transparent hover:bg-muted text-amber-300 hover:text-amber-200 text-xs rounded-full transition-all duration-200 font-medium flex items-center gap-1.5 border border-amber-400 hover:border-amber-300"
                      >
                        {tChat("verify")}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {!hasCurrency && (
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <Check className="h-3 w-3 text-amber-400" />
                        <span className="text-sm text-amber-300">{tChat("selectCurrencyLabel")}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => { router.push("/settings"); handleClose(); }}
                        className="group px-3 py-1.5 bg-transparent hover:bg-muted text-amber-300 hover:text-amber-200 text-xs rounded-full transition-all duration-200 font-medium flex items-center gap-1.5 border border-amber-400 hover:border-amber-300"
                      >
                        {tChat("select")}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {!hasPayPalConfig && (
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <Check className="h-3 w-3 text-amber-400" />
                        <span className="text-sm text-amber-300">{tChat("connectPaypal")}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => { router.push("/settings"); handleClose(); }}
                        className="group px-3 py-1.5 bg-transparent hover:bg-muted text-amber-300 hover:text-amber-200 text-xs rounded-full transition-all duration-200 font-medium flex items-center gap-1.5 border border-amber-400 hover:border-amber-300"
                      >
                        {tChat("connect")}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {!hasShippingMethods && (
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <Check className="h-3 w-3 text-amber-400" />
                        <span className="text-sm text-amber-300">{tChat("addShippingLabel")}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => { router.push("/shipping-settings"); handleClose(); }}
                        className="group px-3 py-1.5 bg-transparent hover:bg-muted text-amber-300 hover:text-amber-200 text-xs rounded-full transition-all duration-200 font-medium flex items-center gap-1.5 border border-amber-400 hover:border-amber-300"
                      >
                        {tChat("setup")}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {!hasAddress && (
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <Check className="h-3 w-3 text-amber-400" />
                        <span className="text-sm text-amber-300">{tChat("addAddressLabel")}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => { router.push("/settings/addresses"); handleClose(); }}
                        className="group px-3 py-1.5 bg-transparent hover:bg-muted text-amber-300 hover:text-amber-200 text-xs rounded-full transition-all duration-200 font-medium flex items-center gap-1.5 border border-amber-400 hover:border-amber-300"
                      >
                        {tChat("add")}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
        <div
          className={cn(
            "flex-1 min-h-0 overflow-y-auto",
            isMobile ? "px-3 pt-4" : "px-6",
          )}
          style={{
            /* 6rem on BOTH branches now: the desktop footer used to be docked
               in flow (5rem was slack, not a reserve); it floats over the body
               since Phase 1.5, so it needs the same reserve as the sheet. */
            paddingBottom: primaryCta
              ? "calc(6rem + env(safe-area-inset-bottom))"
              : isMobile
                ? "5rem"
                : "1.5rem",
          }}
        >
          <div className={cn("pb-2", isMobile ? "px-2" : "px-4")}>
            <AnimatePresence
              mode="wait"
              onExitComplete={() => setVisibleStep(stepTargetRef.current)}
            >
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {step === "photos" ? (
                  <div className="space-y-4">
                    <SimplePhotoEditor
                      key={open ? "chat-sell-item-photos-open" : "chat-sell-item-photos-closed"}
                      ref={photoEditorRef}
                      initialPhotos={photos}
                      onPhotosChangeAction={(updated) => {
                        setPhotos(updated)
                        updateDraft({ photos: updated })
                      }}
                    />
                  </div>
                ) : null}

                {step === "category" ? (
                  <CategoryStep
                    data={draft}
                    updateDataAction={(updates) => updateDraft(updates)}
                    onNextAction={() => {}}
                    onPrevAction={() => {}}
                    hideButton
                    onValidationChange={setIsCategoryValid}
                    compact
                  />
                ) : null}

                {step === "details" ? (
                  <DetailsStep
                    data={draft}
                    updateDataAction={(updates) => updateDraft(updates)}
                    onNextAction={() => {}}
                    onPrevAction={() => {}}
                    hideButton
                    onValidationChange={setIsDetailsValid}
                    compact
                    hideQuantity
                  />
                ) : null}

                {step === "condition" ? (
                  <ConditionStep
                    data={draft}
                    updateDataAction={(updates) => {
                      updateDraft({
                        ...updates,
                        grade: updates.grade === undefined ? "" : (updates.grade as any),
                      })
                    }}
                    onNextAction={() => {}}
                    onPrevAction={() => {}}
                    hideButton
                    compact
                  />
                ) : null}

                {/* ADR_LISTING_SHIP_FROM_ADDRESS Phase 3 — location step removed. */}

                {step === "shipping" ? (
                  <ShippingStep
                    data={draft}
                    updateDataAction={(updates) => updateDraft(updates)}
                    onNextAction={() => {}}
                    onPrevAction={() => {}}
                    onValidationChange={setIsShippingValid}
                    onRegisterSave={(saveFn) => {
                      shippingSaveRef.current = saveFn
                    }}
                    hideButton
                    compact
                  />
                ) : null}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        )}
      </div>
  )

  if (!open) return null

  if (!isMobile) {
    return (
      <DialogCard
        open={open}
        onOpenChange={onOpenChangeAction}
        title={sheetTitle}
        onBack={canGoBack ? goBack : undefined}
        belowTitle={sheetBelowTitle}
        footer={primaryCta ?? undefined}
        panelClassName="max-w-2xl h-[80dvh]"
        /* `content` is a flex column that manages its own scroll (the same
           node the sheet branch mounts), so the card must not nest a second
           scroller with its own bottom reserve around it. */
        /* `content` is a flex column that manages its own scroll (the same
           node the sheet branch mounts), so the card must not nest a second
           scroller with its own bottom reserve around it. */
        scrollManagedByChild
        contentClassName="px-0 pt-0"
      >
        {content}
      </DialogCard>
    )
  }

  return (
    <DockSheet
      open={open}
      onOpenChange={onOpenChangeAction}
      title={sheetTitle}
      onBack={canGoBack ? goBack : undefined}
      belowTitle={sheetBelowTitle}
      footer={primaryCta ?? undefined}
      zIndex={SHEET_Z_NESTED}
      height="95dvh"
      contentClassName="px-0 pt-0"
    >
      {content}
    </DockSheet>
  )
}
